// Session AAO: the office frequency confirmation gate. Shown when a customer accepted a quote at a
// frequency different from the quoted one (payload.frequencyChangePending). Per lift it shows the
// quoted vs requested frequency and lets the office confirm the request or override to another
// valid frequency (the Annual option is disabled where the lift type does not allow it). Each
// candidate's real annual comes from pricing.ts via a server preview. "Confirm and send ESA" binds
// the confirmed frequency to the contract + ESA + Xero draft (POST /quotes/:id/confirm-frequency),
// which is synchronous and human-gated: a partial failure leaves the quote unsent for a retry.
import { useCallback, useEffect, useState } from "react";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { frequencyLabel, frequencyOptionsForType } from "../../lib/quotePricing";
import type { QuoteFrequency } from "../../lib/quotePricing";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

interface LiftRow {
  label: string;
  liftType: string;
  quoted: QuoteFrequency;
  requested: QuoteFrequency;
}

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export function FrequencyConfirmModal({
  quoteId,
  quoteRef,
  onClose,
  onConfirmed,
}: {
  quoteId: string;
  quoteRef: string;
  onClose: () => void;
  onConfirmed: (message: string) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lifts, setLifts] = useState<LiftRow[]>([]);
  const [acceptedTier, setAcceptedTier] = useState<"core" | "care" | "complete">("core");
  const [confirmed, setConfirmed] = useState<QuoteFrequency[]>([]);

  const [previewAnnual, setPreviewAnnual] = useState<number | null>(null);
  const [previewFrequency, setPreviewFrequency] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Load the quote payload: the per-lift quoted + requested frequencies and the accepted tier.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const q = await api.quotes.get(quoteId);
        const p = (q.payload ?? {}) as {
          frequency?: string;
          acceptedTier?: "core" | "care" | "complete";
          lifts?: Array<{ label?: string; liftType?: string; frequency?: string }>;
          customerRequestedFrequencies?: string[];
        };
        const dflt = (p.frequency ?? "quarterly") as QuoteFrequency;
        const rows: LiftRow[] = (p.lifts ?? []).map((l, i) => {
          const quoted = (l.frequency ?? dflt) as QuoteFrequency;
          const requested = (p.customerRequestedFrequencies?.[i] ?? quoted) as QuoteFrequency;
          return { label: (l.label ?? "").trim() || `Lift ${i + 1}`, liftType: l.liftType ?? "custom", quoted, requested };
        });
        if (cancelled) return;
        setLifts(rows);
        setAcceptedTier(p.acceptedTier ?? "core");
        // Default the confirmed selection to what the customer requested.
        setConfirmed(rows.map((r) => r.requested));
      } catch {
        if (!cancelled) setLoadError("Could not load this quote. Please close and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  // Reprice whenever the confirmed selection changes, so the panel shows the real annual.
  const runPreview = useCallback(
    async (freqs: QuoteFrequency[]) => {
      if (freqs.length === 0) return;
      setPreviewing(true);
      setPreviewError(null);
      try {
        const res = await api.quotes.frequencyPreview(quoteId, freqs);
        setPreviewAnnual(res.tiers[acceptedTier]?.annual ?? null);
        setPreviewFrequency(res.frequency);
      } catch (err) {
        setPreviewAnnual(null);
        setPreviewFrequency(null);
        setPreviewError(friendlyApiError(err, "Could not price that frequency."));
      } finally {
        setPreviewing(false);
      }
    },
    [quoteId, acceptedTier],
  );

  useEffect(() => {
    if (confirmed.length > 0) void runPreview(confirmed);
  }, [confirmed, runPreview]);

  function setLiftFrequency(index: number, value: QuoteFrequency) {
    setConfirmed((prev) => prev.map((f, i) => (i === index ? value : f)));
  }

  async function confirmAndSend() {
    setSending(true);
    setSendError(null);
    try {
      const res = await api.quotes.confirmFrequency(quoteId, confirmed);
      onConfirmed(
        `Frequency confirmed for ${quoteRef}. The agreement was sent for signature at ${gbp(res.annualValue)}/yr ex VAT (${frequencyLabel(res.billingFrequency as QuoteFrequency)}).`,
      );
    } catch (err) {
      setSendError(friendlyApiError(err, "Could not confirm and send. Please try again."));
      setSending(false);
    }
  }

  const changed = lifts.some((l, i) => confirmed[i] !== l.quoted);

  return (
    <ModalShell
      ariaLabel={`Confirm service frequency for ${quoteRef}`}
      onClose={() => !sending && onClose()}
      closeOnBackdrop
      width="max-w-[560px]"
      border="border-amber-500"
      panelClassName="p-5 shadow-xl"
    >
        <h2 className="font-heading text-lg text-amber-400">Confirm service frequency</h2>
        <p className="mt-1 text-sm text-ele-body">
          The customer accepted quote {quoteRef} but asked to change the service frequency. Confirm the request or
          override it, then send the agreement for signature. The contract and invoice will use the confirmed frequency.
        </p>

        {loading ? (
          <Loader shape="inline" className="mt-4" />
        ) : loadError ? (
          <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {loadError}
          </p>
        ) : (
          <>
            <div className="mt-4 overflow-hidden rounded-ele border border-ele-purple-accent">
              <table className="w-full text-left text-sm">
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className="px-3 py-2">Lift</th>
                    <th className="px-3 py-2 max-md:hidden">Quoted</th>
                    <th className="px-3 py-2 max-md:hidden">Requested</th>
                    <th className="px-3 py-2">Confirm</th>
                  </tr>
                </thead>
                <tbody>
                  {lifts.map((l, i) => (
                    <tr key={i} className="border-t border-ele-purple-accent">
                      <td className="px-3 py-2 align-middle text-ele-strong">{l.label}</td>
                      <td className="px-3 py-2 align-middle text-ele-body max-md:hidden">{frequencyLabel(l.quoted)}</td>
                      <td className="px-3 py-2 align-middle text-amber-300 max-md:hidden">{frequencyLabel(l.requested)}</td>
                      <td className="px-3 py-2 align-middle">
                        <select
                          value={confirmed[i] ?? l.requested}
                          onChange={(e) => setLiftFrequency(i, e.target.value as QuoteFrequency)}
                          disabled={sending}
                          className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-sm text-ele-strong"
                          aria-label={`Confirmed frequency for ${l.label}`}
                        >
                          {frequencyOptionsForType(l.liftType).map((o) => (
                            <option key={o.value} value={o.value} disabled={o.disabled} title={o.title}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3">
              <p className="text-xs uppercase tracking-widest text-ele-blue">{TIER_LABEL[acceptedTier]} tier total at the confirmed frequency</p>
              {previewError ? (
                <p role="alert" className="mt-1 text-sm text-ele-danger">{previewError}</p>
              ) : previewing ? (
                <p className="mt-1 text-sm text-ele-body">Pricing...</p>
              ) : previewAnnual != null ? (
                <p className="mt-1 font-heading text-2xl text-ele-green">
                  {gbp(previewAnnual)}
                  <span className="ml-2 text-sm font-normal text-ele-body">
                    /yr ex VAT{previewFrequency ? ` (billed ${frequencyLabel(previewFrequency as QuoteFrequency)})` : ""}
                  </span>
                </p>
              ) : (
                <p className="mt-1 text-sm text-ele-body">Adjust a frequency to see the total.</p>
              )}
            </div>

            {sendError && (
              <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
                {sendError}
              </p>
            )}

            <div className={`mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}
              >
                Cancel
              </button>
              <Button className={MODAL_ACTION_BTN} onClick={confirmAndSend} disabled={sending || previewing || previewAnnual == null}>
                {sending ? "Sending..." : changed ? "Confirm and send ESA" : "Send ESA at the quoted frequency"}
              </Button>
            </div>
          </>
        )}
    </ModalShell>
  );
}
